import { Dispatch, FC, SetStateAction } from 'react';
import { Multistep } from 'informed';
import InformedField from '../InformedField';
import Subtitle from '../Subtitle';
import Navigation from '../Navigation';
import { Dropdown, NumberField, SwitcherField, TextField, UploadField } from '@components';
import { ALLOWED_FORMATS, IYupSchema, MAX_FILE_SIZE, STEPS, STEPS_ICONS, STEPS_LABELS } from '../../consts';
import { useRepository } from '@context';
import { GroupBase, OptionsOrGroups } from 'react-select';
import { DataItem, FacilityStatus, MonolithCity, PaginatedResponse } from '@common/interfaces';
import comCss from '../../styles.module.scss';

interface IDetailsStep {
  setYupSchema: Dispatch<SetStateAction<IYupSchema>>;
  countriesOptions: DataItem[];
  depotOptions: DataItem[];
}

const DetailsStep: FC<IDetailsStep> = ({ setYupSchema, countriesOptions, depotOptions }) => {
  const { monolithRepository } = useRepository();

  const loadCityOptions = async (search: string, loadedOptions: OptionsOrGroups<unknown, GroupBase<unknown>>) => {
    const page = Math.floor(loadedOptions.length / 10) + 1;

    const { results, next }: PaginatedResponse<MonolithCity> = await monolithRepository.getCities({
      name_contains: search,
      page,
      page_size: 10,
    });

    return {
      options: results.map(({ name }) => ({ label: name, value: name })),
      hasMore: Boolean(next),
    };
  };

  return (
    <Multistep.Step step={STEPS.DETAILS}>
      <Subtitle text={STEPS_LABELS.DETAILS} icon={STEPS_ICONS.DETAILS} />
      <div className={comCss.row}>
        <InformedField label='Name'>
          <TextField name='name' variant='dark' placeholder='Enter facility name' />
        </InformedField>
        <InformedField label='Workshop status'>
          <SwitcherField name='is_active' activeText={FacilityStatus.ACTIVE} inactiveText={FacilityStatus.ARCHIVED} />
        </InformedField>
      </div>
      <div className={comCss.row}>
        <InformedField label='Country'>
          <Dropdown
            name='country'
            isSearchable
            placeholder='Select country'
            options={countriesOptions}
            variant='dark'
          />
        </InformedField>
        <InformedField label='City name'>
          <Dropdown
            loadOptions={loadCityOptions}
            name='city_name'
            placeholder='Search city...'
            isSearchable
            variant='dark'
          />
        </InformedField>
      </div>
      <div className={comCss.row}>
        <InformedField label='Workshop email'>
          <TextField name='email' variant='dark' placeholder='Enter email' autoComplete='off' />
        </InformedField>
        <InformedField label='FMS Depot ID'>
          <Dropdown name='depot_number' options={depotOptions} variant='dark' placeholder='Select Depot ID' />
        </InformedField>
      </div>
      <InformedField label='Workshop address'>
        <TextField name='address' variant='dark' placeholder='Enter address' autoComplete='off' />
      </InformedField>
      <div className={comCss.row}>
        <InformedField label='Latitude'>
          <NumberField name='latitude' variant='dark' allowDecimal skipFormatting placeholder='Enter latitude' />
        </InformedField>
        <InformedField label='Longitude'>
          <NumberField name='longitude' variant='dark' allowDecimal skipFormatting placeholder='Enter longitude' />
        </InformedField>
      </div>
      <InformedField
        label='Workshop image'
        description={`Max file size is ${MAX_FILE_SIZE} mb. Only ${ALLOWED_FORMATS.join(', ')} files are supported.`}
      >
        <UploadField
          withPreview
          label='Add file'
          accept={ALLOWED_FORMATS.join(', ')}
          name='image'
          maxCount={1}
          maxSize={MAX_FILE_SIZE}
        />
      </InformedField>
      <Navigation setYupSchema={setYupSchema} />
    </Multistep.Step>
  );
};

export default DetailsStep;
